import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to save and restore scroll position for a page
 * Saves scroll position to sessionStorage when navigating away
 * Restores scroll position when returning to the page
 */
export function useScrollRestoration(key) {
  const location = useLocation();
  const scrollKey = `scroll-position-${key}`;
  const isRestoring = useRef(false);

  useEffect(() => {
    // Try to restore scroll position on mount
    const savedPosition = sessionStorage.getItem(scrollKey);
    if (savedPosition) {
      isRestoring.current = true;
      // Use requestAnimationFrame to wait for render
      requestAnimationFrame(() => {
        window.scrollTo(0, parseInt(savedPosition, 10));
        isRestoring.current = false;
      });
    }

    // Save scroll position on scroll (debounced)
    let timeoutId;
    const handleScroll = () => {
      if (isRestoring.current) return;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        sessionStorage.setItem(scrollKey, window.scrollY.toString());
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [scrollKey, location.pathname]);

  // Function to clear saved position (useful after actions that should reset scroll)
  const clearSavedPosition = () => {
    sessionStorage.removeItem(scrollKey);
  };

  return { clearSavedPosition };
}
